import React, { Suspense, useState, useCallback } from 'react';
import { useAppSelector } from '../redux/hooks';
import widgetRegistry from '../widgets/widgetRegistry';
import { EditSaveButton, BackButton, WidgetSelectionModal, ConfirmDeleteModal } from '../components';
import { useDynamicPageWidgets } from '../hooks/useDynamicPageWidgets';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { extractBaseType } from '../utils/pageWidgetUtils';

interface DynamicPageProps {
  pageType: string;
  title?: string;
  backButtonText?: string;
  showBackButton?: boolean;
  showEditButton?: boolean;
  children?: React.ReactNode;
}

const DynamicPage: React.FC<DynamicPageProps> = ({
  pageType,
  title,
  backButtonText = 'Back to Home',
  showBackButton = true,
  showEditButton = true,
  children
}) => {
  const appNavigate = useAppNavigate();
  const {isEditControl } = useAppSelector((state) => state?.editControl);

  // Use the dynamic page widgets hook
  const {
    widgets,
    updateWidgetData,
    toggleEditModeWithSave,
    addWidget,
    deleteWidget,
    isEditMode,
    isSaving,
    hasError,
    hasWidgets
  } = useDynamicPageWidgets(pageType);

  // Modal state
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [pendingAddIndex, setPendingAddIndex] = useState<number | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | undefined>(undefined);

  // Show widget selection modal
  const handleShowAddModal = useCallback((afterIndex?: number) => {
    setPendingAddIndex(afterIndex);
    setShowWidgetModal(true);
  }, []);

  // Handle widget selection from modal
  const handleSelectWidget = useCallback((widgetType: string) => {
    const originalAfterIndex = pendingAddIndex !== undefined ? 
      widgets[pendingAddIndex]?.index : undefined;
    
    // addWidget will handle creating default data and unique ID
    addWidget(widgetType as any, null, originalAfterIndex);
    setShowWidgetModal(false);
    setPendingAddIndex(undefined);
  }, [addWidget, pendingAddIndex, widgets]);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setShowWidgetModal(false);
    setPendingAddIndex(undefined);
  }, []);

  // Show delete confirmation modal
  const handleShowDeleteModal = useCallback((widgetIndex: number) => {
    setPendingDeleteIndex(widgetIndex);
    setShowDeleteModal(true);
  }, []);

  // Confirm delete - actually delete the section
  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteIndex !== undefined) {
      deleteWidget(pendingDeleteIndex);
    }
    setShowDeleteModal(false);
    setPendingDeleteIndex(undefined);
  }, [deleteWidget, pendingDeleteIndex]);

  // Cancel delete
  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setPendingDeleteIndex(undefined);
  }, []);

  // Show Add Section button when no widgets (only in edit mode)
  if (!hasWidgets) {
    return (
      <div className="min-h-screen flex flex-col pt-32 md:pt-36 lg:pt-40">
        {showBackButton && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <BackButton text={backButtonText} onClick={() => appNavigate('/')} />
          </div>
        )}
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            {isEditMode && (
              <button
                onClick={() => handleShowAddModal()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-lg hover:shadow-xl group"
              >
                <FiPlus className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span className="font-medium">Add Section</span>
              </button>
            )}
          </div>
        </div>

        {/* Edit Save Button */}
        {showEditButton && isEditControl && (
          <EditSaveButton
            isEditMode={isEditMode}
            onToggle={toggleEditModeWithSave}
            disabled={isSaving}
            variant="red"
            ariaLabel={isEditMode ? `Save ${pageType} Changes` : `Edit ${pageType}`}
          />
        )}

        {/* Widget Selection Modal */}
        <WidgetSelectionModal
          isOpen={showWidgetModal}
          onClose={handleCloseModal}
          onSelectWidget={handleSelectWidget}
        />
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Error Loading {pageType}</h2>
          <p className="text-gray-600">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pt-32 md:pt-36 lg:pt-40">
      {/* Back Button and Title */}
      {showBackButton && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackButton text={backButtonText} onClick={() => appNavigate('/')} />
          {title && <h1 className="text-4xl font-bold mt-4">{title}</h1>}
        </div>
      )}

      {/* Custom children content (e.g., buttons, CTAs) */}
      {children && (
        <div className="sticky top-0 z-[100] bg-white shadow-md border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            {children}
          </div>
        </div>
      )}

      {/* Dynamic Widget Rendering */}
      {widgets.map((widget, index) => {
        const { type, data } = widget;
        
        // Extract base widget type for registry lookup
        // Handles all formats: new, old, legacy
        // Example: "LeftContentRightMedia_events_A1B2C3D4" -> "LeftContentRightMedia"
        const baseType = extractBaseType(type);
        
        const Component = (widgetRegistry as any)[baseType];
        
        if (!Component) {
          console.warn(`Widget component not found for type: ${type} (base: ${baseType})`);
          return (
            <div key={index} className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Widget Not Found</h2>
                <p className="text-gray-600">Type: {type || 'Unknown'}</p>
                <p className="text-gray-600">Base Type: {baseType}</p>
                <p className="text-gray-600 mt-2">Available: {Object.keys(widgetRegistry).join(', ')}</p>
              </div>
            </div>
          );
        }

        return (
          <div key={`widget-container-${index}`} className="relative">
            <Suspense 
              key={`${type}-${index}`}
              fallback={
                <div className="flex items-center justify-center min-h-screen">
                  <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
                </div>
              }
            >
              <Component 
                data={data}
                isEditing={isEditMode}
                isEditMode={isEditMode}
                onUpdate={(newData: any) => updateWidgetData(index, newData)}
                onCardClick={(item: any) => console.log(`${pageType} item clicked:`, item)}
              />
            </Suspense>

            {/* Add/Delete Section Controls - Always show in edit mode */}
            {isEditMode && (
              <div className="flex justify-center gap-6 py-4 bg-gray-50 border-t border-b border-gray-200">
                <button
                  onClick={() => handleShowAddModal(index)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-lg hover:shadow-xl group"
                  title="Add section after this one"
                >
                  <FiPlus className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-medium text-sm">Add Section</span>
                </button>

                <button
                  onClick={() => handleShowDeleteModal(index)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl group"
                  title="Delete this section"
                >
                  <FiTrash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-medium text-sm">Delete Section</span>
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Edit Save Button */}
      {showEditButton && isEditControl && (
        <EditSaveButton
          isEditMode={isEditMode}
          onToggle={toggleEditModeWithSave}
          disabled={isSaving}
          variant="red"
          ariaLabel={isEditMode ? `Save ${pageType} Changes` : `Edit ${pageType}`}
        />
      )}

      {/* Widget Selection Modal */}
      <WidgetSelectionModal
        isOpen={showWidgetModal}
        onClose={handleCloseModal}
        onSelectWidget={handleSelectWidget}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default DynamicPage;
